import {
  createContext, useContext, useReducer, useEffect,
  useCallback, useMemo, useState, useRef,
} from 'react';
import { PLUGINS } from '@/plugins/registry';
import {
  DEFAULT_FAB_STAGES, DEFAULT_WORK_CENTERS,
} from '@/data/seed';
import {
  supabase, db, realtime,
  sendMessageViaEdge, inviteEmployeeViaEdge, acceptInviteViaEdge,
  registerPushSubscription, sendPushViaEdge,
} from '@/data/supabase';
import { pushSupported, pushPermission, subscribePush, unsubscribePush } from '@/lib/push';

// Local-mode preview is opt-in via VITE_ALLOW_LOCAL_MODE. We no longer auto-
// seed customers/jobs/team in local mode either — empty-state UI is honest
// about there being no data, instead of pretending Pinnacle / Redstone /
// Delta exist.

// ─── Context ──────────────────────────────────────────────────
const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// ─── Initial State ────────────────────────────────────────────
const getInitialState = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('so_state') || '{}');
    const pluginFlags = saved.plugins || {};
    PLUGINS.filter(p => p.core).forEach(p => { pluginFlags[p.id] = true; });
    return {
      customers:    saved.customers    || [],
      jobs:         saved.jobs         || [],
      assemblies:   saved.assemblies   || [],
      pieceMarks:   saved.pieceMarks   || [],
      team:         saved.team         || [],
      timeCards:    saved.timeCards    || [],
      bids:         saved.bids         || [],
      bidLineItems: saved.bidLineItems || [],
      invoices:     saved.invoices     || [],
      fabStages:    saved.fabStages    || DEFAULT_FAB_STAGES,
      workCenters:  saved.workCenters  || DEFAULT_WORK_CENTERS,
      plugins:      pluginFlags,
    };
  } catch {
    return {
      customers: [], jobs: [], assemblies: [], pieceMarks: [],
      team: [], timeCards: [], bids: [], bidLineItems: [], invoices: [],
      fabStages: DEFAULT_FAB_STAGES, workCenters: DEFAULT_WORK_CENTERS,
      plugins: {},
    };
  }
};

// ─── Reducer ──────────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {

    case 'HYDRATE':
      return {
        ...state,
        customers:    action.payload.customers    ?? state.customers,
        jobs:         action.payload.jobs         ?? state.jobs,
        assemblies:   action.payload.assemblies   ?? state.assemblies,
        pieceMarks:   action.payload.pieceMarks   ?? state.pieceMarks,
        team:         action.payload.team         ?? state.team,
        timeCards:    action.payload.timeCards    ?? state.timeCards,
        bids:         action.payload.bids         ?? state.bids,
        bidLineItems: action.payload.bidLineItems ?? state.bidLineItems,
        invoices:     action.payload.invoices     ?? state.invoices,
        fabStages:    action.payload.fabStages    ?? state.fabStages,
        workCenters:  action.payload.workCenters  ?? state.workCenters,
        plugins:      action.payload.plugins      ?? state.plugins,
      };

    // ── Customers ──────────────────────────────────────────────
    case 'ADD_CUSTOMER':
      return { ...state, customers: [...state.customers, action.payload] };
    case 'UPDATE_CUSTOMER':
      return { ...state, customers: state.customers.map(c => c.id === action.payload.id ? { ...c, ...action.payload } : c) };
    case 'DELETE_CUSTOMER':
      return { ...state, customers: state.customers.filter(c => c.id !== action.payload) };
    case 'REPLACE_CUSTOMER':
      return { ...state, customers: state.customers.map(c => c.id === action.payload.oldId ? action.payload.customer : c) };

    // ── Jobs ───────────────────────────────────────────────────
    case 'ADD_JOB':
      return { ...state, jobs: [action.payload, ...state.jobs] };
    case 'UPDATE_JOB':
      return { ...state, jobs: state.jobs.map(j => j.id === action.payload.id ? { ...j, ...action.payload } : j) };
    case 'DELETE_JOB':
      return { ...state, jobs: state.jobs.filter(j => j.id !== action.payload) };
    case 'REPLACE_JOB':
      return { ...state, jobs: state.jobs.map(j => j.id === action.payload.oldId ? action.payload.job : j) };

    // ── Assemblies ─────────────────────────────────────────────
    case 'ADD_ASSEMBLY':
      return { ...state, assemblies: [...state.assemblies, action.payload] };
    case 'UPDATE_ASSEMBLY':
      return { ...state, assemblies: state.assemblies.map(a => a.id === action.payload.id ? { ...a, ...action.payload } : a) };
    case 'DELETE_ASSEMBLY':
      return { ...state, assemblies: state.assemblies.filter(a => a.id !== action.payload) };
    case 'REPLACE_ASSEMBLY':
      return { ...state, assemblies: state.assemblies.map(a => a.id === action.payload.oldId ? action.payload.assembly : a) };

    // ── Piece Marks ────────────────────────────────────────────
    case 'ADD_PIECE_MARK':
      return { ...state, pieceMarks: [...state.pieceMarks, action.payload] };
    case 'UPDATE_PIECE_MARK':
      return { ...state, pieceMarks: state.pieceMarks.map(p => p.id === action.payload.id ? { ...p, ...action.payload } : p) };
    case 'DELETE_PIECE_MARK':
      return { ...state, pieceMarks: state.pieceMarks.filter(p => p.id !== action.payload) };
    case 'REPLACE_PIECE_MARK':
      return { ...state, pieceMarks: state.pieceMarks.map(p => p.id === action.payload.oldId ? action.payload.pieceMark : p) };

    // ── Team / Employees ───────────────────────────────────────
    case 'ADD_EMPLOYEE':
      return { ...state, team: [...state.team, action.payload] };
    case 'UPDATE_EMPLOYEE':
      return { ...state, team: state.team.map(t => t.id === action.payload.id ? { ...t, ...action.payload } : t) };
    case 'DELETE_EMPLOYEE':
      return { ...state, team: state.team.filter(t => t.id !== action.payload) };
    case 'REPLACE_EMPLOYEE':
      return { ...state, team: state.team.map(t => t.id === action.payload.oldId ? action.payload.employee : t) };

    // ── Time Cards ─────────────────────────────────────────────
    case 'ADD_TIME_CARD':
      return { ...state, timeCards: [action.payload, ...state.timeCards] };
    case 'UPDATE_TIME_CARD':
      return { ...state, timeCards: state.timeCards.map(tc => tc.id === action.payload.id ? { ...tc, ...action.payload } : tc) };
    case 'UPSERT_TIME_CARD': {
      const exists = state.timeCards.find(tc => tc.id === action.payload.id);
      if (exists) {
        return { ...state, timeCards: state.timeCards.map(tc => tc.id === action.payload.id ? { ...tc, ...action.payload } : tc) };
      }
      return { ...state, timeCards: [action.payload, ...state.timeCards] };
    }

    // ── Bids ───────────────────────────────────────────────────
    case 'ADD_BID':
      return { ...state, bids: [action.payload, ...state.bids] };
    case 'UPDATE_BID':
      return { ...state, bids: state.bids.map(b => b.id === action.payload.id ? { ...b, ...action.payload } : b) };
    case 'DELETE_BID':
      return {
        ...state,
        bids: state.bids.filter(b => b.id !== action.payload),
        bidLineItems: state.bidLineItems.filter(li => li.bidId !== action.payload),
      };
    case 'REPLACE_BID':
      return {
        ...state,
        bids: state.bids.map(b => b.id === action.payload.oldId ? action.payload.bid : b),
        bidLineItems: state.bidLineItems.map(li => li.bidId === action.payload.oldId ? { ...li, bidId: action.payload.bid.id } : li),
      };

    // ── Bid Line Items ─────────────────────────────────────────
    case 'ADD_BID_LINE_ITEM':
      return { ...state, bidLineItems: [...state.bidLineItems, action.payload] };
    case 'UPDATE_BID_LINE_ITEM':
      return { ...state, bidLineItems: state.bidLineItems.map(li => li.id === action.payload.id ? { ...li, ...action.payload } : li) };
    case 'DELETE_BID_LINE_ITEM':
      return { ...state, bidLineItems: state.bidLineItems.filter(li => li.id !== action.payload) };
    case 'REPLACE_BID_LINE_ITEM':
      return { ...state, bidLineItems: state.bidLineItems.map(li => li.id === action.payload.oldId ? action.payload.lineItem : li) };

    // ── Invoices ───────────────────────────────────────────────
    case 'ADD_INVOICE':
      return { ...state, invoices: [action.payload, ...state.invoices] };
    case 'UPDATE_INVOICE':
      return { ...state, invoices: state.invoices.map(i => i.id === action.payload.id ? { ...i, ...action.payload } : i) };
    case 'REPLACE_INVOICE':
      return { ...state, invoices: state.invoices.map(i => i.id === action.payload.oldId ? action.payload.invoice : i) };
    case 'MARK_PAID':
      return { ...state, invoices: state.invoices.map(i => i.id === action.payload ? { ...i, status: 'paid', paidDate: new Date().toISOString().slice(0, 10) } : i) };

    // ── Plugins ────────────────────────────────────────────────
    case 'TOGGLE_PLUGIN': {
      const plugin = PLUGINS.find(p => p.id === action.payload);
      if (plugin?.core) return state;
      return { ...state, plugins: { ...state.plugins, [action.payload]: !state.plugins[action.payload] } };
    }
    case 'SET_PLUGIN': {
      const plugin = PLUGINS.find(p => p.id === action.payload.id);
      if (plugin?.core) return state;
      return { ...state, plugins: { ...state.plugins, [action.payload.id]: action.payload.enabled } };
    }

    default:
      return state;
  }
}

const isTempId = (id) => typeof id === 'number' && id > 1e10;

// ─── DB row mappers ───────────────────────────────────────────
const customerToDb = (c, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  name: c.name, contact: c.contact, addr: c.addr,
  phone: c.phone, email: c.email, notes: c.notes, status: c.status || 'active',
});
const customerFromDb = (r) => ({
  id: r.id, name: r.name, contact: r.contact, addr: r.addr,
  phone: r.phone, email: r.email, notes: r.notes, status: r.status,
});

const jobToDb = (j, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  job_number: j.jobNumber, customer_id: j.customerId,
  project_name: j.projectName, po_number: j.poNumber,
  status: j.status || 'active', due_date: j.dueDate,
  est_tons: j.estTons, notes: j.notes,
});
const jobFromDb = (r) => ({
  id: r.id, jobNumber: r.job_number, customerId: r.customer_id,
  projectName: r.project_name, poNumber: r.po_number,
  status: r.status, dueDate: r.due_date,
  estTons: r.est_tons, notes: r.notes, createdAt: r.created_at,
});

const assemblyToDb = (a, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  job_id: a.jobId, name: a.name, description: a.description,
});
const assemblyFromDb = (r) => ({
  id: r.id, jobId: r.job_id, name: r.name, description: r.description,
});

const pieceMarkToDb = (pm, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  assembly_id: pm.assemblyId, job_id: pm.jobId,
  mark_number: pm.markNumber, description: pm.description,
  qty: pm.qty, material_spec: pm.materialSpec,
  work_center_id: pm.workCenterId,
  current_stage_id: pm.currentStageId,
  status: pm.status || 'pending',
});
const pieceMarkFromDb = (r) => ({
  id: r.id, assemblyId: r.assembly_id, jobId: r.job_id,
  markNumber: r.mark_number, description: r.description,
  qty: r.qty, materialSpec: r.material_spec,
  workCenterId: r.work_center_id,
  currentStageId: r.current_stage_id,
  status: r.status,
});

const employeeToDb = (e, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  user_id: e.userId ?? null,
  name: e.name, email: e.email, phone: e.phone,
  role: e.role, color: e.color, initials: e.initials,
  active: e.active !== false,
});
const employeeFromDb = (r) => ({
  id: r.id, userId: r.user_id,
  name: r.name, email: r.email, phone: r.phone,
  role: r.role, color: r.color, initials: r.initials,
  active: r.active,
});

const timeCardToDb = (tc, accountId, companyId) => ({
  id: tc.id,
  account_id: accountId, company_id: companyId,
  employee_id: tc.employeeId, job_id: tc.jobId,
  work_center_id: tc.workCenterId,
  punch_type: tc.punchType, punched_at: tc.punchedAt,
  lat: tc.lat ?? null, lng: tc.lng ?? null,
  photo_url: tc.photoUrl ?? null, notes: tc.notes ?? null,
  duration_minutes: tc.durationMinutes ?? null,
});
const timeCardFromDb = (r) => ({
  id: r.id, employeeId: r.employee_id, jobId: r.job_id,
  workCenterId: r.work_center_id,
  punchType: r.punch_type, punchedAt: r.punched_at,
  lat: r.lat, lng: r.lng,
  photoUrl: r.photo_url, notes: r.notes,
  durationMinutes: r.duration_minutes, syncedAt: r.synced_at,
});

const bidToDb = (b, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  bid_number: b.bidNumber, customer_id: b.customerId,
  project_name: b.projectName, bid_date: b.bidDate,
  valid_until: b.validUntil, estimator: b.estimator,
  status: b.status || 'draft', notes: b.notes,
  total_cents: b.totalCents ?? 0,
});
const bidLineItemToDb = (li, accountId, companyId) => ({
  account_id:          accountId,
  company_id:          companyId,
  bid_id:              li.bidId,
  assembly_type:       li.assemblyType || null,
  description:         li.description  || null,
  unit:                li.unit         || 'ea',
  qty:                 Number(li.qty) || 0,
  labor_hrs_unit:      Number(li.laborHrsUnit) || 0,
  material_cost_unit:  Number(li.materialCostUnit) || 0,
  markup_pct:          Number(li.markup) || 0,
  sort_order:          li.sortOrder ?? 0,
});
const bidLineItemFromDb = (r) => ({
  id: r.id, bidId: r.bid_id,
  assemblyType:     r.assembly_type,
  description:      r.description,
  unit:             r.unit,
  qty:              Number(r.qty) || 0,
  laborHrsUnit:     Number(r.labor_hrs_unit) || 0,
  materialCostUnit: Number(r.material_cost_unit) || 0,
  markup:           Number(r.markup_pct) || 0,
  sortOrder:        r.sort_order,
});

const bidFromDb = (r) => ({
  id: r.id, bidNumber: r.bid_number, customerId: r.customer_id,
  projectName: r.project_name, bidDate: r.bid_date,
  validUntil: r.valid_until, estimator: r.estimator,
  status: r.status, notes: r.notes,
  totalCents: r.total_cents, token: r.token, createdAt: r.created_at,
});

const invoiceToDb = (i, accountId, companyId) => ({
  account_id: accountId, company_id: companyId,
  job_id: i.jobId ?? null, customer_id: i.customerId,
  bid_id: i.bidId ?? null,
  amount_cents: i.amount, service: i.service,
  date: i.date, due: i.due, status: i.status || 'sent',
  paid_date: i.paidDate ?? null,
});
const invoiceFromDb = (r) => ({
  id: r.id, jobId: r.job_id, customerId: r.customer_id,
  bidId: r.bid_id,
  amount: r.amount_cents ?? r.amount,
  service: r.service,
  date: r.date, due: r.due, status: r.status,
  paidDate: r.paid_date, token: r.token,
});

// ─── Provider ─────────────────────────────────────────────────
export function AppProvider({ userId = null, userEmail = null, children }) {
  const [state, dispatch] = useReducer(reducer, null, getInitialState);

  const [screen, setScreen]             = useState('dashboard');
  const [mobileScreen, setMobileScreen] = useState('home');
  const [selectedJobId, setSelectedJobId]   = useState(null);
  const [modal, setModal]               = useState(null);
  const [toast, setToast]               = useState(null);
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 768);
  const [forceView, setForceView]       = useState(null);
  const [search, setSearch]             = useState('');
  const [syncStatus, setSyncStatus]     = useState(userId ? 'syncing' : 'local');

  const [companyId, setCompanyId]           = useState(null);
  const [userRole, setUserRole]             = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  // Offline punch queue (written immediately, synced on reconnect)
  const [offlineQueue, setOfflineQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem('so_punch_queue') || '[]'); } catch { return []; }
  });

  const cloudMode          = !!userId && !!supabase;
  const pendingWritesRef   = useRef(0);
  const hydrationDoneRef   = useRef(false);

  // ─── Persist state to localStorage ────────────────────────
  useEffect(() => {
    localStorage.setItem('so_state', JSON.stringify({
      customers:    state.customers,
      jobs:         state.jobs,
      assemblies:   state.assemblies,
      pieceMarks:   state.pieceMarks,
      team:         state.team,
      timeCards:    state.timeCards,
      bids:         state.bids,
      bidLineItems: state.bidLineItems,
      invoices:     state.invoices,
      fabStages:    state.fabStages,
      workCenters:  state.workCenters,
      plugins:      state.plugins,
    }));
  }, [state]);

  // Persist offline punch queue
  useEffect(() => {
    localStorage.setItem('so_punch_queue', JSON.stringify(offlineQueue));
  }, [offlineQueue]);

  // ─── Responsive detection ──────────────────────────────────
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // ─── Toast / Modal helpers ─────────────────────────────────
  const showToast  = useCallback((msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);
  const openModal  = useCallback((type, data = {}) => setModal({ type, data }), []);
  const closeModal = useCallback(() => setModal(null), []);

  // Local-mode plugin defaults (no demo data, just core-plugin flags).
  useEffect(() => {
    if (cloudMode) return;
    hydrationDoneRef.current = true;
    const pluginFlags = { ...state.plugins };
    let dirty = false;
    PLUGINS.filter(p => p.core).forEach(p => {
      if (!pluginFlags[p.id]) { pluginFlags[p.id] = true; dirty = true; }
    });
    if (dirty) dispatch({ type: 'HYDRATE', payload: { plugins: pluginFlags } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudMode]);

  // ─── Cloud bootstrap ───────────────────────────────────────
  useEffect(() => {
    if (!cloudMode) { setSyncStatus('local'); return; }
    let cancelled = false;
    setSyncStatus('syncing');

    (async () => {
      try {
        // Consume any pending invite token in the URL
        try {
          const url = new URL(window.location.href);
          const inviteToken = url.searchParams.get('invite');
          if (inviteToken) {
            await acceptInviteViaEdge({ token: inviteToken });
            url.searchParams.delete('invite');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
          }
        } catch {}

        // Resolve company membership
        let memberRes = await db.company.getForUser(userId);
        let member = memberRes?.data;

        if (!member) {
          const fallbackName = (userEmail || 'My Company').split('@')[0] + ' Fabrication';
          const created = await db.company.create({ name: fallbackName, email: userEmail });
          if (created?.error || !created?.data) throw new Error(created?.error?.message || 'Could not create company');
          const newCompany = created.data;
          await db.members.create({ user_id: userId, company_id: newCompany.id, role: 'owner' });
          member = { company_id: newCompany.id, role: 'owner', company: newCompany };
        }

        if (cancelled) return;
        const cid = member.company_id;
        setCompanyId(cid);
        setUserRole(member.role);
        setCompanyProfile(member.company || null);

        // Load all company-scoped data
        const [custRes, jobRes, empRes, tcRes, bidRes, liRes, invRes, pluginRes] = await Promise.all([
          db.customers.list(cid),
          db.jobs.list(cid),
          db.employees.list(cid),
          db.timeCards.list(cid),
          db.bids.list(cid),
          db.bidLineItems.listForCompany(cid),
          db.invoices.list(cid),
          db.plugins.get(cid),
        ]);

        if (cancelled) return;

        const cloudPlugins = {};
        (pluginRes?.data || []).forEach(row => { cloudPlugins[row.plugin_id] = !!row.enabled; });
        PLUGINS.filter(p => p.core).forEach(p => { cloudPlugins[p.id] = true; });

        dispatch({
          type: 'HYDRATE',
          payload: {
            customers:    (custRes?.data || []).map(customerFromDb),
            jobs:         (jobRes?.data  || []).map(jobFromDb),
            team:         (empRes?.data  || []).map(employeeFromDb),
            timeCards:    (tcRes?.data   || []).map(timeCardFromDb),
            bids:         (bidRes?.data  || []).map(bidFromDb),
            bidLineItems: (liRes?.data   || []).map(bidLineItemFromDb),
            invoices:     (invRes?.data  || []).map(invoiceFromDb),
            plugins:      cloudPlugins,
          },
        });
        setSyncStatus('connected');
        hydrationDoneRef.current = true;
      } catch (err) {
        console.error('Cloud hydrate failed', err);
        if (!cancelled) setSyncStatus('error');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userEmail, cloudMode]);

  // ─── Realtime: fab_stage_events ────────────────────────────
  useEffect(() => {
    if (!cloudMode || !companyId) return;
    const channel = realtime.fabStageEvents(companyId, (payload) => {
      if (!payload?.new) return;
      const ev = payload.new;
      // Advance the piece mark's currentStageId in local state
      if (ev.piece_mark_id && ev.stage_to) {
        dispatch({ type: 'UPDATE_PIECE_MARK', payload: { id: ev.piece_mark_id, currentStageId: ev.stage_to } });
      }
    });
    return () => { try { supabase.removeChannel(channel); } catch {} };
  }, [cloudMode, companyId]);

  // ─── Offline punch sync ────────────────────────────────────
  const syncOfflineQueue = useCallback(async () => {
    if (!cloudMode || !companyId || offlineQueue.length === 0) return;
    const toSync = [...offlineQueue];
    let synced = 0;
    for (const punch of toSync) {
      try {
        const res = await db.timeCards.upsert(timeCardToDb(punch, userId, companyId));
        if (!res?.error) {
          setOfflineQueue(prev => prev.filter(p => p.id !== punch.id));
          dispatch({ type: 'UPSERT_TIME_CARD', payload: { ...punch, syncedAt: new Date().toISOString() } });
          synced++;
        }
      } catch {}
    }
    if (synced > 0) showToast(`${synced} punch${synced > 1 ? 'es' : ''} synced ✓`);
  }, [cloudMode, companyId, userId, offlineQueue, showToast]);

  useEffect(() => {
    const handleOnline = () => syncOfflineQueue();
    const handleVisibility = () => { if (document.visibilityState === 'visible') syncOfflineQueue(); };
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [syncOfflineQueue]);

  // ─── Cloud write helper ────────────────────────────────────
  const runCloudWrite = useCallback(async (fn, onError) => {
    if (!cloudMode) return { ok: true };
    pendingWritesRef.current += 1;
    setSyncStatus('syncing');
    try {
      const res = await fn();
      if (res?.error) throw res.error;
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      if (pendingWritesRef.current === 0) setSyncStatus('connected');
      return { ok: true, data: res?.data };
    } catch (err) {
      console.error('Cloud write failed', err);
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      setSyncStatus('error');
      if (onError) onError(err);
      return { ok: false, error: err };
    }
  }, [cloudMode]);

  const hasPlugin = useCallback((id) => !!state.plugins[id], [state.plugins]);

  // ─── Customer Actions ──────────────────────────────────────
  const addCustomer = useCallback((data) => {
    const tempId = Date.now();
    const customer = { ...data, id: tempId, status: 'active' };
    dispatch({ type: 'ADD_CUSTOMER', payload: customer });
    showToast(`${data.name} added`);
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.customers.create(customerToDb(customer, userId, companyId));
          if (res?.data) dispatch({ type: 'REPLACE_CUSTOMER', payload: { oldId: tempId, customer: customerFromDb(res.data) } });
          return res;
        },
        () => { dispatch({ type: 'DELETE_CUSTOMER', payload: tempId }); showToast('Customer not saved', false); }
      );
    }
    return customer;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const updateCustomer = useCallback((id, patch) => {
    dispatch({ type: 'UPDATE_CUSTOMER', payload: { id, ...patch } });
    if (cloudMode) runCloudWrite(() => db.customers.update(id, patch));
  }, [cloudMode, runCloudWrite]);

  const deleteCustomer = useCallback((id) => {
    const prev = state.customers.find(c => c.id === id);
    dispatch({ type: 'DELETE_CUSTOMER', payload: id });
    showToast('Customer removed');
    if (cloudMode) runCloudWrite(() => db.customers.delete(id), () => { if (prev) dispatch({ type: 'ADD_CUSTOMER', payload: prev }); });
  }, [state.customers, cloudMode, runCloudWrite, showToast]);

  // ─── Job Actions ───────────────────────────────────────────
  const addJob = useCallback((data) => {
    const tempId = Date.now();
    const job = { ...data, id: tempId, status: data.status || 'active', createdAt: new Date().toISOString() };
    dispatch({ type: 'ADD_JOB', payload: job });
    showToast(`Job ${data.jobNumber || ''} created`);
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.jobs.create(jobToDb(job, userId, companyId));
          if (res?.data) {
            const persisted = jobFromDb(res.data);
            dispatch({ type: 'REPLACE_JOB', payload: { oldId: tempId, job: persisted } });
            // Auto-create default assembly
            const asmRes = await db.assemblies.create(assemblyToDb({ jobId: persisted.id, name: 'Main', description: '' }, userId, companyId));
            if (asmRes?.data) dispatch({ type: 'ADD_ASSEMBLY', payload: assemblyFromDb(asmRes.data) });
          }
          return res;
        },
        () => { dispatch({ type: 'DELETE_JOB', payload: tempId }); showToast('Job not saved', false); }
      );
    } else {
      // Local mode: auto-create default assembly
      dispatch({ type: 'ADD_ASSEMBLY', payload: { id: Date.now() + 1, jobId: tempId, name: 'Main', description: '' } });
    }
    return job;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const updateJob = useCallback((id, patch) => {
    dispatch({ type: 'UPDATE_JOB', payload: { id, ...patch } });
    if (cloudMode) runCloudWrite(() => db.jobs.update(id, jobToDb({ ...state.jobs.find(j => j.id === id), ...patch }, userId, companyId)));
  }, [state.jobs, cloudMode, userId, companyId, runCloudWrite]);

  // ─── Piece Mark Actions ────────────────────────────────────
  const addPieceMark = useCallback((data) => {
    const tempId = Date.now();
    const pm = { ...data, id: tempId, status: 'pending', currentStageId: 1 };
    dispatch({ type: 'ADD_PIECE_MARK', payload: pm });
    showToast(`Mark ${data.markNumber} added`);
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.pieceMarks.create(pieceMarkToDb(pm, userId, companyId));
          if (res?.data) dispatch({ type: 'REPLACE_PIECE_MARK', payload: { oldId: tempId, pieceMark: pieceMarkFromDb(res.data) } });
          return res;
        },
        () => { dispatch({ type: 'DELETE_PIECE_MARK', payload: tempId }); showToast('Piece mark not saved', false); }
      );
    }
    return pm;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const advanceStage = useCallback(async (pieceMarkId, stageToId, { note, photoUrl } = {}) => {
    const pm = state.pieceMarks.find(p => p.id === pieceMarkId);
    if (!pm) return;
    const stageFromId = pm.currentStageId;
    dispatch({ type: 'UPDATE_PIECE_MARK', payload: { id: pieceMarkId, currentStageId: stageToId } });

    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          await db.pieceMarks.update(pieceMarkId, { current_stage_id: stageToId });
          await db.fabStageEvents.create({
            account_id:    userId,
            company_id:    companyId,
            piece_mark_id: pieceMarkId,
            job_id:        pm.jobId,
            stage_from:    stageFromId,
            stage_to:      stageToId,
            advanced_by:   userId,
            note:          note || null,
            photo_url:     photoUrl || null,
          });
        },
        () => { dispatch({ type: 'UPDATE_PIECE_MARK', payload: { id: pieceMarkId, currentStageId: stageFromId } }); }
      );
    }
    const stage = state.fabStages.find(s => s.id === stageToId);
    showToast(`Mark ${pm.markNumber} advanced to ${stage?.name || 'next stage'} ✓`);
  }, [state.pieceMarks, state.fabStages, cloudMode, userId, companyId, runCloudWrite, showToast]);

  // ─── Employee Actions ──────────────────────────────────────
  const addEmployee = useCallback((data) => {
    const tempId = Date.now();
    const initials = (data.name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
    const employee = { ...data, id: tempId, initials: data.initials || initials, active: true };
    dispatch({ type: 'ADD_EMPLOYEE', payload: employee });
    showToast(`${data.name} added to team`);
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.employees.create(employeeToDb(employee, userId, companyId));
          if (res?.data) dispatch({ type: 'REPLACE_EMPLOYEE', payload: { oldId: tempId, employee: employeeFromDb(res.data) } });
          return res;
        },
        () => { dispatch({ type: 'DELETE_EMPLOYEE', payload: tempId }); showToast('Employee not saved', false); }
      );
    }
    return employee;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const updateEmployee = useCallback((id, patch) => {
    dispatch({ type: 'UPDATE_EMPLOYEE', payload: { id, ...patch } });
    if (cloudMode) runCloudWrite(() => db.employees.update(id, patch));
    showToast('Employee updated');
  }, [cloudMode, runCloudWrite, showToast]);

  const deleteEmployee = useCallback((id) => {
    const prev = state.team.find(t => t.id === id);
    dispatch({ type: 'DELETE_EMPLOYEE', payload: id });
    showToast('Employee removed');
    if (cloudMode) runCloudWrite(() => db.employees.delete(id), () => { if (prev) dispatch({ type: 'ADD_EMPLOYEE', payload: prev }); });
  }, [state.team, cloudMode, runCloudWrite, showToast]);

  const inviteEmployee = useCallback(async ({ email, role }) => {
    if (!cloudMode) return { ok: false, error: 'Cloud required' };
    const appUrl = typeof window !== 'undefined' ? window.location.origin : null;
    const res = await inviteEmployeeViaEdge({ email, role, appUrl });
    if (!res.ok) showToast(res.error || 'Invite failed', false);
    else showToast(`Invitation sent to ${email} ✓`);
    return res;
  }, [cloudMode, showToast]);

  // ─── Time Card / Clock Actions ─────────────────────────────
  const recordPunch = useCallback((data) => {
    const id = data.id || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const punch = { ...data, id, syncedAt: null };
    dispatch({ type: 'UPSERT_TIME_CARD', payload: punch });

    if (navigator.onLine && cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.timeCards.upsert(timeCardToDb(punch, userId, companyId));
          if (!res?.error) dispatch({ type: 'UPSERT_TIME_CARD', payload: { ...punch, syncedAt: new Date().toISOString() } });
          return res;
        },
        () => {
          setOfflineQueue(prev => [...prev.filter(p => p.id !== id), punch]);
        }
      );
    } else {
      setOfflineQueue(prev => [...prev.filter(p => p.id !== id), punch]);
    }
    return punch;
  }, [cloudMode, userId, companyId, runCloudWrite]);

  // ─── Bid Actions ───────────────────────────────────────────
  const addBid = useCallback((data) => {
    const tempId = Date.now();
    const bid = { ...data, id: tempId, status: 'draft', createdAt: new Date().toISOString() };
    dispatch({ type: 'ADD_BID', payload: bid });
    showToast('Bid drafted');
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.bids.create(bidToDb(bid, userId, companyId));
          if (res?.data) dispatch({ type: 'REPLACE_BID', payload: { oldId: tempId, bid: bidFromDb(res.data) } });
          return res;
        },
        () => { dispatch({ type: 'DELETE_BID', payload: tempId }); showToast('Bid not saved', false); }
      );
    }
    return bid;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const updateBid = useCallback((id, patch) => {
    dispatch({ type: 'UPDATE_BID', payload: { id, ...patch } });
    if (cloudMode) runCloudWrite(() => db.bids.update(id, bidToDb({ ...state.bids.find(b => b.id === id), ...patch }, userId, companyId)));
  }, [state.bids, cloudMode, userId, companyId, runCloudWrite]);

  // ─── Bid Line Items ───────────────────────────────────────
  const addBidLineItem = useCallback((bidId, data) => {
    const tempId = Date.now();
    const lineItem = {
      id: tempId,
      bidId,
      assemblyType:     data.assemblyType || 'misc',
      description:      data.description  || '',
      unit:             data.unit         || 'ea',
      qty:              Number(data.qty) || 1,
      laborHrsUnit:     Number(data.laborHrsUnit) || 0,
      materialCostUnit: Number(data.materialCostUnit) || 0,
      markup:           Number(data.markup) || 20,
      sortOrder:        data.sortOrder ?? 0,
    };
    dispatch({ type: 'ADD_BID_LINE_ITEM', payload: lineItem });
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.bidLineItems.create(bidLineItemToDb(lineItem, userId, companyId));
          if (res?.data) dispatch({ type: 'REPLACE_BID_LINE_ITEM', payload: { oldId: tempId, lineItem: bidLineItemFromDb(res.data) } });
          return res;
        },
        () => { dispatch({ type: 'DELETE_BID_LINE_ITEM', payload: tempId }); showToast('Line item not saved', false); }
      );
    }
    return lineItem;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const updateBidLineItem = useCallback((id, patch) => {
    dispatch({ type: 'UPDATE_BID_LINE_ITEM', payload: { id, ...patch } });
    if (cloudMode) {
      const merged = { ...state.bidLineItems.find(li => li.id === id), ...patch };
      runCloudWrite(() => db.bidLineItems.update(id, bidLineItemToDb(merged, userId, companyId)));
    }
  }, [state.bidLineItems, cloudMode, userId, companyId, runCloudWrite]);

  const deleteBidLineItem = useCallback((id) => {
    dispatch({ type: 'DELETE_BID_LINE_ITEM', payload: id });
    if (cloudMode) runCloudWrite(() => db.bidLineItems.delete(id));
  }, [cloudMode, runCloudWrite]);

  // Send a bid: flip status to 'sent'. The PDF function + tokenized public
  // link are separate calls because they only work in cloud mode.
  const sendBid = useCallback((id) => {
    const bid = state.bids.find(b => b.id === id);
    if (!bid) return;
    updateBid(id, { status: 'sent' });
    showToast('Bid marked sent — share the public link with the GC.');
  }, [state.bids, updateBid, showToast]);

  // Asks the bid-pdf-generate edge function to produce + sign a PDF URL.
  // Returns { ok, url, error }. Owner-only in cloud mode.
  const generateBidPdf = useCallback(async (bidId) => {
    if (!cloudMode || !supabase) return { ok: false, error: 'Sign in to the cloud to generate PDFs.' };
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) return { ok: false, error: 'Not signed in.' };
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/bid-pdf-generate`, {
        method: 'POST',
        headers: {
          Authorization:  `Bearer ${token}`,
          apikey:         supabase.supabaseKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bid_id: bidId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: body?.error || `HTTP ${res.status}` };
      return { ok: true, url: body?.signed_url };
    } catch (e) {
      return { ok: false, error: String(e?.message || e) };
    }
  }, [cloudMode]);

  // ─── Invoice Actions ───────────────────────────────────────
  const addInvoice = useCallback((data) => {
    const tempId = Date.now();
    const invoice = {
      ...data,
      id:       tempId,
      date:     data.date     || new Date().toISOString().slice(0, 10),
      status:   data.status   || 'sent',
      paidDate: data.paidDate || null,
    };
    dispatch({ type: 'ADD_INVOICE', payload: invoice });
    showToast('Invoice created');
    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.invoices.create(invoiceToDb(invoice, userId, companyId));
          if (res?.data) dispatch({ type: 'REPLACE_INVOICE', payload: { oldId: tempId, invoice: invoiceFromDb(res.data) } });
          return res;
        },
        () => showToast('Invoice not saved', false)
      );
    }
    return invoice;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const markPaid = useCallback((invId) => {
    dispatch({ type: 'MARK_PAID', payload: invId });
    showToast('Marked as paid ✓');
    if (cloudMode) runCloudWrite(() => db.invoices.update(invId, { status: 'paid', paid_date: new Date().toISOString().slice(0, 10) }));
  }, [cloudMode, runCloudWrite, showToast]);

  // ─── Plugin Actions ────────────────────────────────────────
  const togglePlugin = useCallback((id) => {
    const plugin = PLUGINS.find(p => p.id === id);
    if (plugin?.core) return;
    const next = !state.plugins[id];
    dispatch({ type: 'TOGGLE_PLUGIN', payload: id });
    if (cloudMode) {
      runCloudWrite(
        () => db.plugins.set(companyId, userId, id, next),
        () => dispatch({ type: 'SET_PLUGIN', payload: { id, enabled: !next } })
      );
    }
  }, [state.plugins, cloudMode, userId, companyId, runCloudWrite]);

  // ─── Company Profile ───────────────────────────────────────
  const updateCompanyProfile = useCallback(async (patch) => {
    if (!cloudMode || !companyId) { showToast('Cloud required', false); return { ok: false }; }
    setCompanyProfile(prev => prev ? { ...prev, ...patch } : patch);
    const res = await db.company.update(companyId, patch);
    if (res?.error) { showToast('Save failed', false); return { ok: false, error: res.error }; }
    showToast('Company profile saved ✓');
    return { ok: true };
  }, [cloudMode, companyId, showToast]);

  const retrySync = useCallback(() => { if (cloudMode) setSyncStatus('connected'); }, [cloudMode]);

  // ─── Push notifications ────────────────────────────────────
  const [pushStatus, setPushStatus] = useState(() => pushSupported() ? pushPermission() : 'unsupported');

  const enablePush = useCallback(async () => {
    if (!cloudMode) { showToast('Sign in required', false); return { ok: false }; }
    try {
      const sub = await subscribePush();
      const res = await registerPushSubscription(sub);
      if (!res.ok) { showToast(res.error || 'Push registration failed', false); return { ok: false }; }
      setPushStatus('granted');
      showToast('Push notifications enabled ✓');
      return { ok: true };
    } catch (e) {
      setPushStatus(pushPermission());
      showToast(e.message || 'Push failed', false);
      return { ok: false };
    }
  }, [cloudMode, showToast]);

  const disablePush = useCallback(async () => {
    await unsubscribePush();
    setPushStatus('default');
    showToast('Push notifications disabled');
  }, [showToast]);

  const pingPush = useCallback(({ title, body, url, tag, targetUserId } = {}) => {
    if (!cloudMode) return;
    sendPushViaEdge({ targetUserId, title, body, url, tag }).catch(() => {});
  }, [cloudMode]);

  // ─── Computed values ───────────────────────────────────────
  // All money is in CENTS (amount_cents). Display layers convert to dollars.
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const activeJobs       = state.jobs.filter(j => j.status === 'active').length;
    const piecesInProgress = state.pieceMarks.filter(p => p.status === 'in_progress').length;
    const piecesShipped    = state.pieceMarks.filter(p => p.currentStageId === 8 || p.status === 'complete').length;
    // Treat invoices as overdue if explicitly tagged OR sent + past-due date.
    const isOverdue = (i) => i.status === 'overdue' || (i.status === 'sent' && i.due && i.due < today);
    const overdueInvoices = state.invoices.filter(isOverdue);
    const overdueCents    = overdueInvoices.reduce((a, i) => a + (Number(i.amount) || 0), 0);
    const overdueCount    = new Set(overdueInvoices.map(i => i.customerId)).size;
    const pendingBids     = state.bids.filter(b => b.status === 'sent').length;

    // Labor hours this week (clock_in/clock_out pairs)
    const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
    const weekCards = state.timeCards.filter(tc => tc.punchType === 'clock_out' && tc.punchedAt > weekAgo);
    const laborHrsWeek = Math.round(weekCards.reduce((a, tc) => a + (tc.durationMinutes || 0), 0) / 60);

    return {
      activeJobs, piecesInProgress, piecesShipped,
      overdueAmount: Math.round(overdueCents / 100),  // dollars for display
      overdueCount, pendingBids, laborHrsWeek,
      totalCustomers: state.customers.length,
    };
  }, [state]);

  const jobById   = useCallback((id) => state.jobs.find(j => j.id === id), [state.jobs]);
  const custById  = useCallback((id) => state.customers.find(c => c.id === id), [state.customers]);
  const stageById = useCallback((id) => state.fabStages.find(s => s.id === id), [state.fabStages]);
  const wcById    = useCallback((id) => state.workCenters.find(w => w.id === id), [state.workCenters]);
  const empById   = useCallback((id) => state.team.find(e => e.id === id), [state.team]);

  const isReallyMobile = forceView ? forceView === 'mobile' : isMobile;

  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  const value = {
    // State
    customers:    state.customers,
    jobs:         state.jobs,
    assemblies:   state.assemblies,
    pieceMarks:   state.pieceMarks,
    team:         state.team,
    timeCards:    state.timeCards,
    bids:         state.bids,
    bidLineItems: state.bidLineItems,
    invoices:     state.invoices,
    fabStages:    state.fabStages,
    workCenters:  state.workCenters,
    plugins:      state.plugins,
    // UI
    screen, setScreen,
    mobileScreen, setMobileScreen,
    selectedJobId, setSelectedJobId,
    modal, openModal, closeModal,
    toast, showToast,
    isMobile: isReallyMobile,
    forceView, setForceView,
    search, setSearch,
    isOnline,
    offlineQueue,
    // Auth / Sync
    userId, userEmail, cloudMode, syncStatus, retrySync,
    // Multi-user
    companyId, userRole, companyProfile, updateCompanyProfile,
    inviteEmployee,
    // Computed
    stats, jobById, custById, stageById, wcById, empById,
    // Actions
    hasPlugin, togglePlugin,
    addCustomer, updateCustomer, deleteCustomer,
    addJob, updateJob,
    addPieceMark, advanceStage,
    addEmployee, updateEmployee, deleteEmployee,
    recordPunch, syncOfflineQueue,
    addBid, updateBid, sendBid, generateBidPdf,
    addBidLineItem, updateBidLineItem, deleteBidLineItem,
    addInvoice, markPaid,
    sendMessage: async ({ to, channel, subject, body, customerId }) => {
      if (!cloudMode) { showToast('Cloud required', false); return { ok: false }; }
      const res = await sendMessageViaEdge({ to, channel, subject, body, customerId });
      if (res.ok) showToast(`${channel === 'email' ? 'Email' : 'SMS'} sent ✓`);
      else showToast(`Send failed: ${res.error}`, false);
      return res;
    },
    pushStatus, enablePush, disablePush, pingPush,
    dispatch,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

